import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { NvBadge, nvAgentDetailPath } from "@/components/navigator";
import { NvStarFilledIcon, NvStarOutlineIcon } from "@/components/navigator/nv-icons-mypage";
import { cn } from "@/lib/utils";
import type { FinderCard } from "@/services/navigator/finder-service";

// My Page 카드 (Figma ver.7 "Card / Agent (My Page)", 북마크 · 비교함 · 최근 조회 공통 · 제안 14 · 01):
// 282 x 254, r12, 안쪽 20 · 간격 8. 위 줄 56 = [로고 박스 56 | 배지 + 북마크(또는 휴지통)], 제품명 2줄 자리(48), 공급사 1줄(20), 소개 2줄(40).
// 배지 유무와 상관없이 카드 높이가 같아요. 로고는 56 흰 박스 안 40 영역에 비율 유지(contain), 없으면 이니셜 타일.

// 이니셜 타일 색: Figma 예시(청록 · 보라 · 빨강 · 민트)를 에이전트 id로 고르게 나눠요 (가정 A46)
const TILE_COLORS = [
  { bg: "#E6F4F2", fg: "#0F766E" },
  { bg: "#F3E8FF", fg: "#7E22CE" },
  { bg: "#FDECEC", fg: "#DC2626" },
  { bg: "#E3F7F4", fg: "#0D9488" },
];

function tileColorOf(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return TILE_COLORS[hash % TILE_COLORS.length];
}

export function initialOf(name: string) {
  return name.trim().slice(0, 1).toUpperCase();
}

function CardLogo({ card }: { card: FinderCard }) {
  const color = tileColorOf(card.id);
  return (
    <span aria-hidden className="flex h-14 w-14 shrink-0 items-center justify-center rounded-nv-card border border-nv-bg-muted bg-nv-surface">
      {card.logo_url ? (
        <img src={card.logo_url} alt="" className="h-10 w-10 object-contain" />
      ) : (
        <span
          className="flex h-10 w-10 items-center justify-center rounded-nv-md text-[18px] font-bold leading-[22px]"
          style={{ backgroundColor: color.bg, color: color.fg }}
        >
          {initialOf(card.name)}
        </span>
      )}
    </span>
  );
}

export interface MyAgentCardProps {
  card: FinderCard;
  // 위 줄 오른쪽: 북마크 별 · 휴지통 (Certified 배지는 이 왼쪽에 카드가 붙여요)
  action?: ReactNode;
  // 카드 바깥 왼쪽 위에 겹치는 요소 (비교함 선택 체크박스)
  corner?: ReactNode;
  // 비교함에서 선택된 카드: 1.5px 주황 테두리
  selected?: boolean;
}

export function MyAgentCard({ card, action, corner, selected = false }: MyAgentCardProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-2 rounded-nv-card bg-nv-surface p-5",
        selected ? "border-[1.5px] border-nv-brand" : "border border-nv-border",
      )}
    >
      {corner}
      <div className="flex h-14 items-start justify-between">
        <CardLogo card={card} />
        <div className="flex h-[31px] items-center gap-[10px]">
          {card.is_certified && <NvBadge>PwC Certified</NvBadge>}
          {action}
        </div>
      </div>
      <div className="h-1" />
      {/* 카드 전체가 눌리도록 제품명 링크를 카드 위로 펼쳐요 (북마크 · 배지 · 체크는 z-10으로 그 위). 전체 이름은 툴팁 */}
      <Link
        to={nvAgentDetailPath(card.id)}
        title={card.name}
        className="line-clamp-2 min-h-12 text-[17px] font-medium leading-6 text-nv-text-primary after:absolute after:inset-0 after:content-['']"
      >
        {card.name}
      </Link>
      <p className="min-h-5 truncate text-[13px] leading-5 text-nv-text-secondary" title={card.supplier_name ?? undefined}>
        {card.supplier_name}
      </p>
      <div className="h-1" />
      <p className="line-clamp-2 min-h-10 text-[13px] leading-5 text-nv-text-secondary">{card.summary ?? ""}</p>
    </div>
  );
}

// 북마크 별: 북마크됨 = 주황 채움, 아님 = 윤곽 (제안 01 · 14). 21 x 21
export function MyStarButton({ on, onToggle, label }: { on: boolean; onToggle: () => void; label?: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label ?? (on ? "북마크 해제" : "북마크")}
      onClick={onToggle}
      className={cn("relative z-10 inline-flex h-[21px] w-[21px] shrink-0 items-center justify-center transition-opacity", !on && "hover:opacity-60")}
    >
      {on ? <NvStarFilledIcon /> : <NvStarOutlineIcon />}
    </button>
  );
}
